import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";

const DETECTORS_TO_SKIP = "detectors_to_skip";
const ANALYZERS_TO_SKIP = "analyzers_to_skip";
const CVES_COLLECTORS_TO_SKIP = "cves_collectors_to_skip";

const ManageCustomerFeatureFlagsTable = () => {
  const colors = useColors();
  const {
    getAllCustomersFeatureFlags,
    updateCustomerFeatureFlags,
    addCustomerFeatureFlag,
    addCustomerFeatureFlagsForAllCustomers,
    getCustomerFeatureFlagOptions,
    getDetectionOptions,
    getControlOptions,
    deleteCustomerFeatureFlag,
  } = useAPI();

  const [featureFlagOptions, setFeatureFlagOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerFeatureFlags, setCustomerFeatureFlags] = useState({});
  const [rows, setRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFeatureFlags, setEditedFeatureFlags] = useState({});

  const [detectionOptions, setDetectionOptions] = useState({});
  const [availableDetectionIds, setAvailableDetectionIds] = useState([]);
  const [controlOptions, setControlOptions] = useState({});
  const [availableControlIds, setAvailableControlIds] = useState([]);
  const [availableCollectorTools, setAvailableCollectorTools] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFeatureFlagName, setNewFeatureFlagName] = useState("");
  const [newFeatureFlagValue, setNewFeatureFlagValue] = useState("No");
  const [newFeatureFlagCustomer, setNewFeatureFlagCustomer] = useState("");
  const [selectedDetectionIds, setSelectedDetectionIds] = useState([]);
  const [selectedControlIds, setSelectedControlIds] = useState([]);
  const [selectedCollectorTools, setSelectedCollectorTools] = useState([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [flagToDelete, setFlagToDelete] = useState(null);

  /***************************
   *  Fetch feature flag options and detection options
   ***************************/
  useEffect(() => {
    const fetchOptions = async () => {
      const [flagsResponse, detectionsResponse, controlsResponse] =
        await Promise.all([
          getCustomerFeatureFlagOptions(),
          getDetectionOptions(),
          getControlOptions(),
        ]);

      setFeatureFlagOptions(flagsResponse.data);
      setDetectionOptions(detectionsResponse.data || {});
      setControlOptions(controlsResponse.data || {});

      // Extract detection IDs from the options for the dropdown
      const detectionIds = Object.keys(detectionsResponse.data || {});
      setAvailableDetectionIds(detectionIds);

      // Extract control IDs from the options for the dropdown
      const controlIds = Object.keys(controlsResponse.data || {});
      setAvailableControlIds(controlIds);

      // Define available CVE collector tools
      const collectorTools = ["wiz", "orca", "crowdstrike", "ox", "qualys"];
      setAvailableCollectorTools(collectorTools);
    };

    fetchOptions();
  }, []);

  /***************************
   *  Fetch existing flags
   ***************************/
  useEffect(() => {
    const fetchFlags = async () => {
      const response = await getAllCustomersFeatureFlags();
      setCustomerFeatureFlags(response.data);
    };
    fetchFlags();
  }, []);

  /****************************************************************
   *  Whenever selectedCustomer changes, build the grid rows
   ****************************************************************/
  useEffect(() => {
    if (selectedCustomer) {
      const flagsObj = customerFeatureFlags[selectedCustomer] || {};
      const newRows = Object.entries(flagsObj).map(([feature, value], idx) => ({
        id: idx,
        feature,
        enabled:
          feature === DETECTORS_TO_SKIP ||
          feature === ANALYZERS_TO_SKIP ||
          feature === CVES_COLLECTORS_TO_SKIP
            ? Array.isArray(value) && value.length > 0
              ? `Disabled: ${value.join(", ")}`
              : "All Enabled"
            : value
            ? "Yes"
            : "No",
        rawValue: value, // Store the raw value for easier access
      }));
      setRows(newRows);
      setEditedFeatureFlags({ ...flagsObj });
    } else {
      setRows([]);
      setEditedFeatureFlags({});
    }
  }, [customerFeatureFlags, selectedCustomer]);

  /****************************************************
   *  If editing ends, refresh from the server
   ****************************************************/
  useEffect(() => {
    if (!isEditing) {
      const fetchFlags = async () => {
        const response = await getAllCustomersFeatureFlags();
        setCustomerFeatureFlags(response.data);
      };
      fetchFlags();
    }
  }, [isEditing]);

  /**********************************
   *   Handlers
   **********************************/
  const handleCustomerChange = (event) => {
    setSelectedCustomer(event.target.value);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    if (!selectedCustomer) return;
    await updateCustomerFeatureFlags(selectedCustomer, editedFeatureFlags);
    setCustomerFeatureFlags((prev) => ({
      ...prev,
      [selectedCustomer]: { ...editedFeatureFlags },
    }));
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    if (selectedCustomer) {
      setEditedFeatureFlags(customerFeatureFlags[selectedCustomer] || {});
    }
    setIsEditing(false);
  };

  const handleFeatureFlagChange = (feature) => (event) => {
    if (
      feature === DETECTORS_TO_SKIP ||
      feature === ANALYZERS_TO_SKIP ||
      feature === CVES_COLLECTORS_TO_SKIP
    ) {
      const value = event.target.value
        ? event.target.value
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id !== "")
        : [];
      setEditedFeatureFlags((prev) => ({
        ...prev,
        [feature]: value,
      }));
    } else {
      setEditedFeatureFlags((prev) => ({
        ...prev,
        [feature]: event.target.value === "Yes",
      }));
    }
  };

  /**********************************
   *   Modal (Add Feature Flag)
   **********************************/
  const handleOpenModal = () => {
    setIsModalOpen(true);
    // Reset modal form
    setNewFeatureFlagName("");
    setNewFeatureFlagValue("No");
    setNewFeatureFlagCustomer("");
    setSelectedDetectionIds([]);
    setSelectedControlIds([]);
    setSelectedCollectorTools([]);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleAddFeatureFlag = async () => {
    try {
      let flagValue;
      if (newFeatureFlagName === DETECTORS_TO_SKIP) {
        flagValue = selectedDetectionIds;
      } else if (newFeatureFlagName === ANALYZERS_TO_SKIP) {
        flagValue = selectedControlIds;
      } else if (newFeatureFlagName === CVES_COLLECTORS_TO_SKIP) {
        flagValue = selectedCollectorTools;
      } else {
        flagValue = newFeatureFlagValue === "Yes";
      }

      // If user wants to add to ALL customers:
      if (newFeatureFlagCustomer === "all") {
        await addCustomerFeatureFlagsForAllCustomers({
          [newFeatureFlagName]: flagValue,
        });
      } else {
        // Otherwise add to the chosen single customer
        await addCustomerFeatureFlag(newFeatureFlagCustomer, {
          [newFeatureFlagName]: flagValue,
        });
      }

      // Re-fetch to see the changes in UI
      const response = await getAllCustomersFeatureFlags();
      setCustomerFeatureFlags(response.data);
      Toast.success("Feature flag added successfully!");

      handleCloseModal();
    } catch (err) {
      Toast.error(`Error adding feature flag: ${err}`);
    }
  };

  // Update the formatDetectionOption function to show more info
  const formatDetectionOption = (id) => {
    if (detectionOptions[id]) {
      const { title, severity } = detectionOptions[id];
      return {
        id,
        label: `${id}: ${title} (Severity: ${severity})`,
        severity,
      };
    }
    return { id, label: id };
  };

  // Format control options to show more info
  const formatControlOption = (id) => {
    if (controlOptions[id]) {
      const { title, tool } = controlOptions[id];
      return {
        id,
        label: `${id}: ${title} (Tool: ${tool}})`,
        tool,
      };
    }
    return { id, label: id };
  };

  /**********************************
   *   Delete Feature Flag
   **********************************/
  const handleDeleteClick = (feature) => {
    setFlagToDelete(feature);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setFlagToDelete(null);
  };

  const handleDeleteFeatureFlag = async () => {
    try {
      if (!selectedCustomer || !flagToDelete) return;

      await deleteCustomerFeatureFlag(selectedCustomer, flagToDelete);

      // Update local state after successful deletion
      setCustomerFeatureFlags((prev) => {
        const updated = { ...prev };
        const customerFlags = { ...updated[selectedCustomer] };
        delete customerFlags[flagToDelete];
        updated[selectedCustomer] = customerFlags;
        return updated;
      });

      Toast.success("Feature flag deleted successfully!");
      setIsDeleteModalOpen(false);
      setFlagToDelete(null);

      // Also update the rows
      setRows(rows.filter((row) => row.feature !== flagToDelete));
    } catch (error) {
      Toast.error(`Error deleting feature flag: ${error.message}`);
    }
  };

  /**********************************
   *   Columns for the DataGrid
   **********************************/
  const columns = [
    { field: "feature", headerName: "Feature", width: 220 },
    {
      field: "enabled",
      headerName: "Enabled",
      width: 350,
      renderCell: (params) => {
        if (params.row.feature === DETECTORS_TO_SKIP) {
          return isEditing ? (
            <Autocomplete
              multiple
              options={availableDetectionIds}
              getOptionLabel={(option) => formatDetectionOption(option).label}
              value={
                Array.isArray(editedFeatureFlags[params.row.feature])
                  ? editedFeatureFlags[params.row.feature]
                  : []
              }
              onChange={(e, newValue) => {
                setEditedFeatureFlags((prev) => ({
                  ...prev,
                  [params.row.feature]: newValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select detection IDs to disable"
                  size="small"
                />
              )}
              renderOption={(props, option) => {
                const { label, severity } = formatDetectionOption(option);
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      "&:hover": { backgroundColor: colors.grey_lighter },
                    }}
                  >
                    <Chip
                      size="small"
                      label={option}
                      sx={{
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2" noWrap sx={{ ml: 1 }}>
                      {detectionOptions[option]?.title || `Detection ${option}`}
                    </Typography>
                  </Box>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Tooltip
                    key={index}
                    title={
                      detectionOptions[option]?.title || `Detection ${option}`
                    }
                  >
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      sx={{
                        margin: "2px",
                      }}
                    />
                  </Tooltip>
                ))
              }
              sx={{ width: "100%" }}
              size="small"
            />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {Array.isArray(params.row.rawValue) &&
              params.row.rawValue.length > 0 ? (
                params.row.rawValue.map((id) => (
                  <Tooltip
                    key={id}
                    title={detectionOptions[id]?.title || `Detection ${id}`}
                  >
                    <Chip
                      label={id}
                      size="small"
                      sx={{
                        color: colors.common_black,
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Chip
                  label="All Enabled"
                  size="small"
                  sx={{
                    color: colors.common_black,
                  }}
                />
              )}
            </Box>
          );
        } else if (params.row.feature === ANALYZERS_TO_SKIP) {
          return isEditing ? (
            <Autocomplete
              multiple
              options={availableControlIds}
              getOptionLabel={(option) => formatControlOption(option).label}
              value={
                Array.isArray(editedFeatureFlags[params.row.feature])
                  ? editedFeatureFlags[params.row.feature]
                  : []
              }
              onChange={(e, newValue) => {
                setEditedFeatureFlags((prev) => ({
                  ...prev,
                  [params.row.feature]: newValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select control IDs to disable"
                  size="small"
                />
              )}
              renderOption={(props, option) => {
                const { label, tool } = formatControlOption(option);
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      "&:hover": { backgroundColor: colors.grey_lighter },
                    }}
                  >
                    <Chip
                      size="small"
                      label={option}
                      sx={{
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2" noWrap sx={{ ml: 1 }}>
                      {controlOptions[option]?.title || `Control ${option}`}
                    </Typography>
                  </Box>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Tooltip
                    key={index}
                    title={controlOptions[option]?.title || `Control ${option}`}
                  >
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      sx={{
                        margin: "2px",
                      }}
                    />
                  </Tooltip>
                ))
              }
              sx={{ width: "100%" }}
              size="small"
            />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {Array.isArray(params.row.rawValue) &&
              params.row.rawValue.length > 0 ? (
                params.row.rawValue.map((id) => (
                  <Tooltip
                    key={id}
                    title={controlOptions[id]?.title || `Control ${id}`}
                  >
                    <Chip
                      label={id}
                      size="small"
                      sx={{
                        color: colors.common_black,
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Chip
                  label="All Enabled"
                  size="small"
                  sx={{
                    color: colors.common_black,
                  }}
                />
              )}
            </Box>
          );
        } else if (params.row.feature === CVES_COLLECTORS_TO_SKIP) {
          return isEditing ? (
            <Autocomplete
              multiple
              freeSolo
              options={availableCollectorTools}
              getOptionLabel={(option) => option}
              value={
                Array.isArray(editedFeatureFlags[params.row.feature])
                  ? editedFeatureFlags[params.row.feature]
                  : []
              }
              onChange={(e, newValue) => {
                setEditedFeatureFlags((prev) => ({
                  ...prev,
                  [params.row.feature]: newValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type custom CVE collectors to skip"
                  size="small"
                />
              )}
              renderOption={(props, option) => {
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      "&:hover": { backgroundColor: colors.grey_lighter },
                    }}
                  >
                    <Chip
                      size="small"
                      label={option}
                      sx={{
                        mr: 1,
                      }}
                    />
                  </Box>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Tooltip key={index} title={`CVE Collector: ${option}`}>
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      size="small"
                      sx={{
                        margin: "2px",
                      }}
                    />
                  </Tooltip>
                ))
              }
              sx={{ width: "100%" }}
              size="small"
            />
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {Array.isArray(params.row.rawValue) &&
              params.row.rawValue.length > 0 ? (
                params.row.rawValue.map((tool) => (
                  <Tooltip key={tool} title={`CVE Collector: ${tool}`}>
                    <Chip
                      label={tool}
                      size="small"
                      sx={{
                        color: colors.common_black,
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Chip
                  label="All Enabled"
                  size="small"
                  sx={{
                    color: colors.common_black,
                  }}
                />
              )}
            </Box>
          );
        }

        return isEditing ? (
          <TextField
            select
            value={editedFeatureFlags[params.row.feature] ? "Yes" : "No"}
            onChange={handleFeatureFlagChange(params.row.feature)}
            fullWidth
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>
        ) : (
          <Chip
            label={params.value}
            size="small"
            sx={{
              color: colors.common_black,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={() => handleDeleteClick(params.row.feature)}
            size="small"
            disabled={isEditing}
          >
            <DeleteIcon
              color={isEditing ? "disabled" : "error"}
              fontSize="small"
            />
          </IconButton>
        );
      },
    },
  ];

  /**********************************
   *   Render
   **********************************/
  return (
    <Box sx={{ marginTop: "16px", width: "90%", maxWidth: "800px" }}>
      <Paper elevation={2} sx={{ padding: 2, marginBottom: 2 }}>
        <Typography variant="h6" gutterBottom>
          Manage Customer Feature Flags
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
          {/* Select Customer */}
          <FormControl sx={{ width: 200, marginRight: 2 }}>
            <InputLabel id="customer-select-label">Customer</InputLabel>
            <Select
              labelId="customer-select-label"
              value={selectedCustomer || ""}
              onChange={handleCustomerChange}
            >
              {Object.keys(customerFeatureFlags).map((customerId) => (
                <MenuItem key={customerId} value={customerId}>
                  {customerId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Edit / Save / Cancel Buttons */}
          <SecondaryButton
            onClick={isEditing ? handleSaveClick : handleEditClick}
            disabled={!rows.length || !selectedCustomer}
            sx={{ marginRight: 1 }}
          >
            {isEditing ? "Save" : "Edit"}
          </SecondaryButton>

          {isEditing && (
            <SecondaryButton
              onClick={handleCancelClick}
              sx={{ marginRight: 1 }}
            >
              Cancel
            </SecondaryButton>
          )}

          {/* Add Feature Flag */}
          <SecondaryButton onClick={handleOpenModal}>
            Add Feature Flag
          </SecondaryButton>
        </Box>
      </Paper>

      {/* Data Grid */}
      <StyledDataGrid
        rows={rows}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        autoHeight
        sx={{
          "& .MuiDataGrid-cell": {
            padding: "8px",
          },
        }}
      />

      {/* Modal */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: colors.common_white,
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Add New Feature Flag
          </Typography>

          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel id="modal-customer-select-label">Customer</InputLabel>
            <Select
              labelId="modal-customer-select-label"
              value={newFeatureFlagCustomer}
              onChange={(e) => setNewFeatureFlagCustomer(e.target.value)}
            >
              <MenuItem value="all">All Customers</MenuItem>
              {Object.keys(customerFeatureFlags).map((customerId) => (
                <MenuItem key={customerId} value={customerId}>
                  {customerId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel id="feature-flag-name-label">
              Feature Flag Name
            </InputLabel>
            <Select
              labelId="feature-flag-name-label"
              value={newFeatureFlagName}
              onChange={(e) => setNewFeatureFlagName(e.target.value)}
            >
              {featureFlagOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ marginTop: 2 }}>
            {newFeatureFlagName === DETECTORS_TO_SKIP ? (
              <Autocomplete
                multiple
                options={availableDetectionIds}
                getOptionLabel={(option) => formatDetectionOption(option).label}
                value={selectedDetectionIds}
                onChange={(e, newValue) => {
                  setSelectedDetectionIds(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Detection IDs to Disable"
                    variant="outlined"
                    placeholder="Select detection IDs to disable"
                    helperText="Select the detection IDs you want to disable"
                  />
                )}
                renderOption={(props, option) => {
                  const { label, severity } = formatDetectionOption(option);
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "&:hover": { backgroundColor: colors.grey_lighter },
                      }}
                    >
                      <Chip
                        size="small"
                        label={option}
                        sx={{
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {detectionOptions[option]?.title ||
                          `Detection ${option}`}
                      </Typography>
                    </Box>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Tooltip
                      key={index}
                      title={
                        detectionOptions[option]?.title || `Detection ${option}`
                      }
                    >
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{
                          margin: "2px",
                        }}
                      />
                    </Tooltip>
                  ))
                }
              />
            ) : newFeatureFlagName === ANALYZERS_TO_SKIP ? (
              <Autocomplete
                multiple
                options={availableControlIds}
                getOptionLabel={(option) => formatControlOption(option).label}
                value={selectedControlIds}
                onChange={(e, newValue) => {
                  setSelectedControlIds(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Control IDs to Disable"
                    variant="outlined"
                    placeholder="Select control IDs to disable"
                    helperText="Select the control IDs you want to disable"
                  />
                )}
                renderOption={(props, option) => {
                  const { label, tool } = formatControlOption(option);
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "&:hover": { backgroundColor: colors.grey_lighter },
                      }}
                    >
                      <Chip
                        size="small"
                        label={option}
                        sx={{
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {controlOptions[option]?.title || `Control ${option}`}
                      </Typography>
                    </Box>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Tooltip
                      key={index}
                      title={
                        controlOptions[option]?.title || `Control ${option}`
                      }
                    >
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{
                          margin: "2px",
                        }}
                      />
                    </Tooltip>
                  ))
                }
              />
            ) : newFeatureFlagName === CVES_COLLECTORS_TO_SKIP ? (
              <Autocomplete
                multiple
                freeSolo
                options={availableCollectorTools}
                getOptionLabel={(option) => option}
                value={selectedCollectorTools}
                onChange={(e, newValue) => {
                  setSelectedCollectorTools(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="CVE Collectors to Skip"
                    variant="outlined"
                    placeholder="Select or type custom CVE collectors to skip"
                    helperText="Select from list or type custom tool names and press Enter"
                  />
                )}
                renderOption={(props, option) => {
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "&:hover": { backgroundColor: colors.grey_lighter },
                      }}
                    >
                      <Chip
                        size="small"
                        label={option}
                        sx={{
                          mr: 1,
                        }}
                      />
                    </Box>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Tooltip key={index} title={`CVE Collector: ${option}`}>
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                        sx={{
                          margin: "2px",
                        }}
                      />
                    </Tooltip>
                  ))
                }
              />
            ) : (
              <>
                <InputLabel id="feature-flag-value-label">Value</InputLabel>
                <Select
                  labelId="feature-flag-value-label"
                  value={newFeatureFlagValue}
                  onChange={(e) => setNewFeatureFlagValue(e.target.value)}
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </>
            )}
          </FormControl>

          <Box
            sx={{ marginTop: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleCloseModal}
              sx={{ marginRight: 2 }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddFeatureFlag}
              variant="contained"
              disabled={!newFeatureFlagCustomer || !newFeatureFlagName}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteModalOpen} onClose={handleDeleteModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: colors.common_white,
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Delete Feature Flag
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete the feature flag{" "}
            <strong>{flagToDelete}</strong> for customer{" "}
            <strong>{selectedCustomer}</strong>?
          </Typography>
          <Box
            sx={{ marginTop: 3, display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              onClick={handleDeleteModalClose}
              sx={{ marginRight: 2 }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteFeatureFlag}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ManageCustomerFeatureFlagsTable;
